import { CurrencyPipe } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, Output, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { CONSTANTS, NumberUtils } from '@pk/powerkioskutils';

import * as _ from 'lodash';
import moment from 'moment-timezone';
import { ToastrService } from 'ngx-toastr';

import { GraphqlService } from 'src/app/graphql/graphql.service';
import { LoadFactorService } from 'src/app/load-factor/load-factor.service';
import { Contract, EsignDocument, MarginOption, ServiceTypeUnit, User } from 'src/app/shared/models';
import { AbstractPageForm } from 'src/app/shared/pages/page-form.abstract';

@Component({
	selector: 'pk-broker-notification',
	templateUrl: './notification.component.html',
	styleUrls: ['./notification.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class NotificationComponent extends AbstractPageForm {
	@Input() contract: Contract;
	@Input() loggedInUser: User;
	@Input() hideCloseButton: boolean;
	@Input() isLoadFactor: boolean;
	@Input() isNYMEXAlert: boolean;
	@Input() isMarketClosedAlert: boolean;
	@Input() isSmall: boolean;
	@Input() hide: boolean;

	//For automated renewal
	@Input() serviceTypeUnit: ServiceTypeUnit;
	@Input() pageTitle: string;

	//For reinstatement pop-up
	@Input() esignDocumentDownloading: boolean;
	@Output() reviewAndSendReinstatementClicked: EventEmitter<boolean> = new EventEmitter();
	@Output() reinstatementDownloadFormClicked: EventEmitter<boolean> = new EventEmitter();

	@ViewChild('alert') private alert: ElementRef;

	public show = true;
	public header = '';
	public headerClass = '';
	public iconClass = '';
	public helperLink = '';
	public marginOptions: MarginOption[];
	public hasDroppedRejectedAccounts = false;
	public isReinstatementEsignActive = true;
	public reinstatementText = 'Start Reinstatement Process';

	constructor(
		private loadFactorService: LoadFactorService,
		private fb: FormBuilder,
		private graphqlService: GraphqlService,
		private toastrService: ToastrService,
		private currencyPipe: CurrencyPipe,
	) {
		super();
		this.submitText = 'Send Email';
	}

	public async loadPageData() {
		if (this.showAutomatedRenewal) {
			const result = await this.graphqlService.getMarginOptions();
			this.marginOptions =
				this.contract?.serviceTypeId === this.CONSTANTS.serviceTypes.electricity.serviceTypeId
					? result.data.marginOptions.electricityMarginOptions
					: result.data.marginOptions.gasMarginOptions;
		}

		if (this.contract && this.contract.esigns) {
			let esignDocument: EsignDocument;
			const esigns = _.orderBy(this.contract.esigns, 'addDate', 'desc');
			for (const esign of esigns) {
				esignDocument = esign.documents.find(
					d =>
						d.document.isEsign &&
						(d.document.attachment.attachmentTypeId === CONSTANTS.attachmentTypes.reinstatementForm ||
							d.document.attachment.attachmentTypeId === CONSTANTS.attachmentTypes.generalReinstatementForm),
				);

				if (esignDocument) {
					break;
				}
			}

			// mark as true to show up the notification if no reinstatement esign exists or the esign is active
			this.isReinstatementEsignActive = esignDocument ? (esignDocument.document.isActive ? true : false) : true;
			this.hasDroppedRejectedAccounts = _.some(
				this.contract.locations,
				l => l.status === CONSTANTS.contractLocationStatuses.dropped || l.status === CONSTANTS.contractLocationStatuses.rejection,
			);
			if (this.contract.isReinstatementSent) {
				this.reinstatementText = 'Resend Reinstatement';
			}
		}
	}

	public getForm() {
		return this.fb.group({
			margin: [NumberUtils.round(this.contract?.margin, 6), [Validators.required]],
		});
	}

	public async onFormSubmitted() {
		await this.graphqlService.sendAutomatedRenewalEmail(this.contract?.renewalContract?.contractId, this.margin);
		this.hideAlert();
		this.toastrService.success('Your proposal has been sent. A copy will also be sent to you.', this.pageTitle);
	}

	get showAutomatedRenewal() {
		return this.contract?.showAutomatedRenewalNotification;
	}

	get margin() {
		return this.form.margin.value;
	}

	get notificationText(): string {
		if (this.contract) {
			if (this.hasDroppedRejectedAccounts && this.isReinstatementEsignActive) {
				if (!_.some(this.contract.locations, l => l.status === CONSTANTS.contractLocationStatuses.dropped)) {
					// if only have rejected account
					this.header = 'Accounts have been rejected';
				} else {
					this.header = 'Accounts have been dropped';
				}
			}
			if (this.showAutomatedRenewal) {
				this.header = 'Your renewal pricing is ready';
			}
			if (this.isLoadFactor) {
				this.header = 'Our Advice:';
				return this.loadFactorService.getAdvice(this.contract);
			}
			return this.getContractNotificationText();
		} else if (this.isNYMEXAlert) {
			this.header = 'Pricing Help:';
			return this.getNYMEXNotificationText();
		} else if (this.isMarketClosedAlert) {
			this.header = 'Pricing Help:';
			return 'During weekends/holidays, the market is closed and any pricing done is subject to rate change.';
		}

		return '';
	}

	get alertIcon(): string {
		if (this.contract) {
			if (this.hasDroppedRejectedAccounts && this.isReinstatementEsignActive) {
				return 'exclamation-triangle';
			}

			return 'exclamation-circle';
		}

		return 'comments-dollar';
	}

	get isEnterpiseAgent(): boolean {
		return this.loggedInUser?.agent && this.loggedInUser?.agent?.isEnterprise;
	}

	public getNYMEXNotificationText(): string {
		return (
			`<p>Experiencing a significant dip in prices, some contracts are trading at 52-week lows. Now is a great time to buy.` +
			`<strong><a href="https://assets.powerkiosk.com/includes/market/Why-Procure.pdf" target="_blank" download>` +
			` See more here.</a></strong></p>`
		);
	}

	private getRecentDroppedClawbackTotalAmount() {
		const droppedLocations = this.contract.locations.filter(l => l.status === CONSTANTS.contractLocationStatuses.dropped);
		let totalClawbackAmount = 0;
		for (const location of droppedLocations) {
			const commissionPayablesSchedules = this.contract.commissionPayablesSchedules.filter(
				c =>
					c.contractLocationId === location.contractLocationId &&
					moment(c.dropDate).format('YYYY-MM-DD') === moment(location.dropDate).format('YYYY-MM-DD'),
			);
			totalClawbackAmount += _.sumBy(commissionPayablesSchedules, c => c.amount);
		}
		return NumberUtils.round(totalClawbackAmount, 2);
	}

	public getContractNotificationText(): string {
		// for now return nothing
		if (this.contract?.isResidential) {
			return '';
		}

		const email = this.loggedInUser.agent && this.loggedInUser.agent.parentId ? this.loggedInUser.agent.parent.email : 'deals@powerkiosk.com';
		const validMatrixDate = moment(this.contract?.contractDate).format('MM/DD/YYYY');
		const validUploadDate = moment(this.contract?.uploadDate ? this.contract?.uploadDate : new Date()).format('MM/DD/YYYY');
		const hasCustomerContractEsignRequestEmail =
			this.contract.agent.userNotificationCategories?.some(
				cat => cat.id === CONSTANTS.userNotificationCategories.customerContractESignRequestEmail,
			) &&
			this.contract.customer.userNotificationCategories?.some(
				cat => cat.id === CONSTANTS.userNotificationCategories.customerContractESignRequestEmail,
			);

		if (this.hasDroppedRejectedAccounts && this.isReinstatementEsignActive) {
			const clawbackAmount = this.getRecentDroppedClawbackTotalAmount();
			const displayAmount = this.currencyPipe.transform(Math.abs(clawbackAmount), this.contract.state.country.isoCode, 'symbol', '1.2-2');

			return (
				`Please review below in Locations to see which accounts have been dropped. ` +
				`You can send your customer an authorization email to have them reinstated. ` +
				(clawbackAmount < 0 ? `Failure to reinstate these accounts may impact your commissions by ${displayAmount}.` : '')
			);
		}

		switch (this.contract?.status) {
			case CONSTANTS.statuses.confirmed: {
				if (this.showAutomatedRenewal) {
					return (
						'Send your customer the best rate and a proposal via email. ' + 'Simply select your margin for the new pricing and click "Send Email".'
					);
				}
				break;
			}
			case CONSTANTS.statuses.acomp: {
				if (this.contract?.awardDate && !this.contract?.uploadDate && this.contract?.supplierId) {
					// award bid
					return (
						`The contract has been awarded to ${this.contract?.supplier.name}.  ` +
						`It can take up to <strong>three business hours</strong> for the contract to be emailed to ` +
						`your client for signature. If after that time has passed and your customer has not received ` +
						`an email for signature, please email <a href="mailto:${email}">${email}</a> with your ` +
						`inquiry and include the contract number for reference.`
					);
				} else if (this.contract?.awardDate && this.contract?.uploadDate && this.contract?.supplierId) {
					if (this.contract?.isSentToCustomer) {
						if (hasCustomerContractEsignRequestEmail) {
							return (
								`This contract has been successfully sent to the customer for signature via email. ` +
								`Remind them that the contract is valid until 5PM CT on ${validUploadDate}.`
							);
						} else {
							return (
								`This customer has unsubscribed from the Contract E-Sign Request email.  You will need to send the contract manually or contact support for assistance. ` +
								`Remind them that the contract is valid until 5PM CT on ${validUploadDate}.`
							);
						}
					} else {
						return (
							`This contract has been successfully downloaded and a copy was sent to your email. Please remember ` +
							`that pricing is valid until 5PM CT on ${validUploadDate}.`
						);
					}
				}
				break;
			}
			case CONSTANTS.statuses.ainp: {
				if (!this.contract?.rfqSession.isOnHold) {
					return (
						`Your pricing session was successfully started. Your pricing session is set to end on ` +
						`${this.contract?.rfqSession.formatEndDate()} CT. ` +
						`Since pricing is only valid until close of business the day it's provided, suppliers typically ` +
						`provide pricing the day a pricing session ends to ensure it's valid. Check back for pricing at that time.`
					);
				} else {
					return `Our Operations Staff is reviewing your pricing session and will reach out with any questions as necessary.`;
				}
			}
			case CONSTANTS.statuses.quote: {
				if (this.contract?.supplierId && !this.contract?.isResidential) {
					// select matrix
					if (this.contract?.isSentToCustomer) {
						if (hasCustomerContractEsignRequestEmail) {
							return (
								`This contract has been successfully sent to the customer for signature via email. ` +
								`Remind them pricing is valid until 5PM CT on ${validMatrixDate}.`
							);
						} else {
							return (
								`This customer has unsubscribed from the Contract E-Sign Request email.  You will need to send the contract manually or contact support for assistance. ` +
								`Remind them pricing is valid until 5PM CT on ${validMatrixDate}.`
							);
						}
					} else {
						return (
							`This contract has been successfully downloaded and a copy was sent to your email. Please remember ` +
							`that pricing is valid until 5PM CT on ${validUploadDate}.`
						);
					}
				}
				break;
			}
			case CONSTANTS.statuses.rejected:
			case CONSTANTS.statuses.dropped: {
				if (this.contract?.rejectReason) {
					return this.contract?.rejectReason;
				}
				break;
			}
			case CONSTANTS.statuses.signed: {
				if (this.contract?.isResidential && !this.contract?.externalId) {
					return (
						`This contract has been authorized by the customer for enrollment, and now we are waiting on the supplier ` +
						`to confirm this contract.  This can typically take 2-3 business days for non-immediate switches, and between ` +
						`1-2 days for immediate switches please submit a support ticket if this contract is still not confirmed if ` +
						`that time has passed.`
					);
				}
				break;
			}
			case CONSTANTS.statuses.expired: {
				if (this.contract?.supplierId && this.contract?.isResidential && !this.contract?.externalId) {
					return this.contract?.rejectReason;
				} else if (this.contract?.supplierId) {
					return (
						`This contract is no longer valid, please create a support ticket with any questions regarding` +
						` this contract and include the contract number in your inquiry.`
					);
				}
				break;
			}
		}

		return '';
	}

	public hideAlert(): void {
		this.show = false;
		setTimeout(() => {
			this.alert.nativeElement.remove();
		}, 1000);
	}

	public async reviewAndSendReinstatement() {
		this.reviewAndSendReinstatementClicked.emit(true);
	}

	public async reinstatementDownloadForm() {
		this.reinstatementDownloadFormClicked.emit(true);
	}
}
